import { EntityType } from '@frontend/common';
import { CheckboxInput, DateInput, PasswordInput } from '@frontend/components/basic-forms';
import { Form } from '@frontend/components/forms';
import { Spinner } from '@frontend/elements';
import { IdentificationClient } from '@frontend/identification/api';
import { Identification, IdentificationType, UpdateIdentification } from '@frontend/identification/types';
import { CommonMessage } from '@frontend/lang';
import { updateField } from '@frontend/repository';
import { EntitySelect } from '@frontend/workflow/components';

import { useAppDispatch } from '../../redux/store';
import useIdentificationUpdate, { FORM_ID } from './identification-update.controller';

const ID = 'identification-update';

const IdentificationUpdate = () => {
    const viewProps = useIdentificationUpdate();
    const dispatch = useAppDispatch();
    const supportsIndefinitely = viewProps.identification && ![IdentificationType.TAG, IdentificationType.BADGE].includes(viewProps.identification.type);
    if (!viewProps.identification) {
        return <Spinner />;
    }
    return (
        <div id={ID}>
            <Form<Identification, UpdateIdentification>
                id={FORM_ID}
                useStaticId
                type={EntityType.IDENTIFICATION}
                value={viewProps.formValues}
                dispatch={dispatch}
                object={viewProps.identification}
                headerOverwrite={viewProps.identification.type.replace(/_/g, ' ')}
                onCancel={viewProps.onCancel}
                onSubmit={(v) => IdentificationClient.patchIdentification(viewProps.identification!.account_id, viewProps.identification!.id, v)}
                onSubmitSuccess={viewProps.onSubmitSuccess}
                show={['key', 'valid_timestamp']}
                required={['key']}
                fieldOverwrite={
                    supportsIndefinitely
                        ? new Map([
                              [
                                  'valid_timestamp',
                                  <div className='flex w-full flex-col gap-2'>
                                      <CheckboxInput
                                          id='identification-indefinitely'
                                          label={CommonMessage.OBJECTS.IDENTIFICATION.INDEFINITELY}
                                          submitted={false}
                                          value={viewProps.isValidIndefinitely}
                                          onChange={(isChecked) => {
                                              if (isChecked) {
                                                  const copy = { ...viewProps.formValues };
                                                  delete copy.valid_timestamp;
                                                  viewProps.setFormValues(copy);
                                                  dispatch(updateField({ formId: FORM_ID, key: 'valid_timestamp', value: null }));
                                              }
                                              viewProps.changeIsValidIndefinitely(isChecked);
                                          }}
                                      />
                                      {!viewProps.isValidIndefinitely && (
                                          <DateInput
                                              id='identification-valid-timestamp'
                                              label='Valid'
                                              submitted={false}
                                              value={viewProps.formValues.valid_timestamp ? new Date(viewProps.formValues.valid_timestamp) : undefined}
                                              onChange={(value) =>
                                                  dispatch(updateField({ formId: FORM_ID, key: 'valid_timestamp', value: value?.toISOString() ?? null }))
                                              }
                                              dateTime
                                          />
                                      )}
                                  </div>
                              ]
                          ])
                        : undefined
                }>
                <EntitySelect
                    submitted={false}
                    value={{ entity_type: viewProps.formValues.entity_type ?? undefined, entity_id: viewProps.formValues.entity_id ?? undefined }}
                    onChange={({ entity_type, entity_id }) => {
                        dispatch(updateField({ formId: FORM_ID, key: 'entity_type', value: entity_type ?? null }));
                        dispatch(updateField({ formId: FORM_ID, key: 'entity_id', value: entity_id ?? null }));
                    }}
                    dispatch={dispatch}
                />
                {[IdentificationType.BASIC_AUTH, IdentificationType.API_KEY].includes(viewProps.identification.type) && (
                    <PasswordInput
                        label='Secret'
                        value={viewProps.formValues.secret ?? undefined}
                        onChange={(v) => {
                            dispatch(updateField({ formId: FORM_ID, key: 'secret', value: v }));
                        }}
                    />
                )}
            </Form>
        </div>
    );
};

export default IdentificationUpdate;
